// caiet UC93-04/06/07/08 — the filter state of the submission register, kept
// pure so the correlation rule is provable without rendering anything. The URL
// is the source of truth for that state (UC93-01), and the URL is hand-editable,
// so every value is validated against what the session can actually see before
// it reaches a request.
import { businessDate, isCalendarDay } from '@simsm/shared-types';
import type { SubmissionPlace } from '../../../features/reports/api';

export type PeriodId = 'week' | 'last30' | 'currentMonth' | 'custom';

export const PERIOD_IDS: readonly PeriodId[] = ['week', 'last30', 'currentMonth', 'custom'];

export const DEFAULT_PERIOD: PeriodId = 'week';

export interface Period {
  id: PeriodId;
  fromIso: string;
  toIso: string;
}

export interface RegisterFilterChoice {
  registrarIdno: string;
  regionId: string;
  localityId: string;
  siteIds: string[];
}

export interface FilterOption {
  id: string;
  label: string;
  // A site the catalogue has inactivated: offered, and marked as such.
  inactive?: boolean;
}

export interface CorrelatedFilters {
  registrars: FilterOption[];
  regions: FilterOption[];
  localities: FilterOption[];
  sites: FilterOption[];
  applied: RegisterFilterChoice;
}

// CF-14: "today" is the calendar date in Chisinau, not on the reader's clock,
// so the default week ends on the same day the server counts it in
// (docs/shape-uc-93-submission-register.md §Business day, filter values and
// history, §1; the shared business-date helper, docs/shape-ops-business-date.md D2).
export function todayIso(now: Date = new Date()): string {
  return businessDate(now);
}

// A real calendar day. The URL is hand-editable, and `2026-02-30` would
// otherwise reach the server as a date it cannot cast.
export function isCalendarDate(iso: string): boolean {
  return isCalendarDay(iso);
}

export function addDays(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return shifted.toISOString().slice(0, 10);
}

// UC93-04: the register opens on the last week, meaning the six days before
// today plus today. A custom range only counts once both ends are real calendar
// days in order; until then the period stays incomplete rather than sending a
// range the user cannot explain.
export function resolvePeriod(
  periodId: string,
  fromIso: string,
  toIso: string,
  today: string,
): Period {
  if (
    periodId === 'custom' &&
    isCalendarDate(fromIso) &&
    isCalendarDate(toIso) &&
    fromIso <= toIso
  ) {
    return { id: 'custom', fromIso, toIso };
  }
  if (periodId === 'custom') {
    return { id: 'custom', fromIso: '', toIso: '' };
  }
  if (periodId === 'last30') {
    return { id: 'last30', fromIso: addDays(today, -29), toIso: today };
  }
  if (periodId === 'currentMonth') {
    return { id: 'currentMonth', fromIso: `${today.slice(0, 8)}01`, toIso: today };
  }
  return { id: 'week', fromIso: addDays(today, -6), toIso: today };
}

function collect(
  facets: SubmissionPlace[],
  id: (facet: SubmissionPlace) => string,
  label: (facet: SubmissionPlace) => string,
  inactive: (facet: SubmissionPlace) => boolean = () => false,
): FilterOption[] {
  const byId = new Map<string, FilterOption>();
  for (const facet of facets) {
    const key = id(facet);
    if (!key || byId.has(key)) continue;
    const option: FilterOption = { id: key, label: label(facet) || key };
    if (inactive(facet)) option.inactive = true;
    byId.set(key, option);
  }
  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label, 'ro'));
}

// UC93-08: each list is derived from the places still reachable after the
// filters above it (every place the role may see, reported or not, UC93-07),
// and a chosen value that those places no longer contain is dropped instead of
// silently emptying the register.
export function correlateFilters(
  facets: SubmissionPlace[],
  chosen: RegisterFilterChoice,
): CorrelatedFilters {
  const registrars = collect(
    facets,
    (f) => f.registrarIdno,
    (f) => f.registrarName,
  );
  const registrarIdno = registrars.some((o) => o.id === chosen.registrarIdno)
    ? chosen.registrarIdno
    : '';

  const inRegistrar = facets.filter((f) => !registrarIdno || f.registrarIdno === registrarIdno);
  const regions = collect(
    inRegistrar,
    (f) => f.regionId,
    (f) => f.regionName,
  );
  const regionId = regions.some((o) => o.id === chosen.regionId) ? chosen.regionId : '';

  const inRegion = inRegistrar.filter((f) => !regionId || f.regionId === regionId);
  const localities = collect(
    inRegion,
    (f) => f.localityId,
    (f) => f.localityName,
  );
  const localityId = localities.some((o) => o.id === chosen.localityId) ? chosen.localityId : '';

  const inLocality = inRegion.filter((f) => !localityId || f.localityId === localityId);
  const sites = collect(
    inLocality,
    (f) => f.siteId,
    (f) => f.siteName,
    (f) => f.siteActive === false,
  );
  const siteIds = chosen.siteIds.filter((id) => sites.some((o) => o.id === id));

  return {
    registrars,
    regions,
    localities,
    sites,
    applied: { registrarIdno, regionId, localityId, siteIds },
  };
}
